import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus } from "lucide-react";
import { z } from "zod";
import { createSuperuserSuperusersPost } from "@/client-admin";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { cn, getErrorDetails, SUPERUSER_PERMISSIONS, superUserPermissionSchema } from "@/lib/utils";

const createSuperUserSchema = z.object({
  email: z.string().email(),
  first_name: z.string().min(1, "First name is required").max(255),
  last_name: z.string().min(1, "Last name is required").max(255),
  permissions: z.array(superUserPermissionSchema).min(1, "At least one permission is required"),
});

type CreateSuperUserFormValues = z.infer<typeof createSuperUserSchema>;

export function CreateSuperUserDialog() {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const form = useForm<CreateSuperUserFormValues>({
    resolver: zodResolver(createSuperUserSchema),
    defaultValues: {
      email: "",
      first_name: "",
      last_name: "",
      permissions: [],
    },
  });

  const { mutateAsync: createSuperUser, isPending } = useMutation({
    mutationFn: (data: CreateSuperUserFormValues) =>
      createSuperuserSuperusersPost({
        requestBody: {
          user_in: {
            email: data.email,
            first_name: data.first_name,
            last_name: data.last_name,
            is_active: true,
            is_superuser: true,
          },
          client_links: [
            {
              // client_id is overridden server-side with the admin client
              client_id: 0,
              permissions: data.permissions,
              estimated_price_update_preferences: null,
              cc_recipients: null,
              bcc_recipients: null,
            },
          ],
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["superusers"] });
      toast({
        title: "Success",
        description: "Superuser created successfully",
      });
      setOpen(false);
      form.reset();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: getErrorDetails(error, true),
        variant: "destructive",
      });
    },
  });

  const onSubmit = async (data: CreateSuperUserFormValues) => {
    await createSuperUser(data);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
        form.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button className="flex gap-2">
          <Plus className="h-4 w-4" />
          Create Superuser
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Create New Superuser</DialogTitle>
          <DialogDescription>
            The superuser is linked to the admin client and receives a welcome email with a generated password.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" placeholder="Enter email" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="first_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First Name</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Enter first name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="last_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last Name</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Enter last name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="permissions"
              render={({ field }) => (
                <FormItem>
                  <div className="mb-4">
                    <FormLabel className="flex items-center justify-between">
                      Permissions
                      {form.formState.errors.permissions && (
                        <span className="text-sm font-normal text-destructive">
                          {form.formState.errors.permissions.message}
                        </span>
                      )}
                    </FormLabel>
                    <FormDescription>Select the permissions for this superuser.</FormDescription>
                  </div>
                  <div
                    className={cn(
                      "grid grid-cols-2 gap-4",
                      form.formState.errors.permissions && "rounded-md border border-destructive/50 p-4",
                    )}
                  >
                    {SUPERUSER_PERMISSIONS.map((permission) => (
                      <FormItem key={permission.id} className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value?.includes(permission.id)}
                            onCheckedChange={(checked) => {
                              const currentPermissions = field.value || [];
                              return checked
                                ? field.onChange([...currentPermissions, permission.id])
                                : field.onChange(currentPermissions.filter((value) => value !== permission.id));
                            }}
                          />
                        </FormControl>
                        <FormLabel className="font-normal">{permission.label}</FormLabel>
                      </FormItem>
                    ))}
                  </div>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setOpen(false);
                  form.reset();
                }}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? "Creating..." : "Create Superuser"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
